import { graphql } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import Helmet from 'react-helmet';

import { Layout } from '../../components/Layout';
import { ExperienceLayout, ExperiencePagination } from '../../components/Experience';
import GlobalStyles from '../components/Layout/GlobalStyles';
import { getKey } from '../helpers';

// ********************************************************************************
// == LEGACY ======================================================================
// This is a legacy component, it is not recommended to use it along with the new
// project structure and best practices. Once the component is refactored and it no
// longer relies on legacy code, it can be safely removed.
// ................................................................................
// The code below is almost identical from that used in the legacy project, with
// some exceptions:
// - Localization is handled by the i18next plugin
// - The use of the Layout component to wrap the page content
// - Global styles are imported locally, since the new project uses other styling
//   solutions
// SEE: gatsby-node.js
/**
 * Experience template
 * @param {*} props - component props
 * @returns {JSX}
 */
const Experience = (props) => {
  const {
    pageContext: {
      // language: locale,
      elements,
      pages,
      page,
      categories,
      category,
      tag,
      tags,
      social,
    },
  } = props;

  // Locale is obtained from the i18next plugin
  let { language: locale } = useI18next();
  if (!locale) locale = 'en';

  const label = getKey(props, `pageContext.${locale}MetaTitle`);
  let basePath = `/${locale}/experience`;
  const base = `/${locale}/experience`;

  const options = categories.map((item) => ({
    label: getKey(item, `node.category_${locale}`),
    value: base + getKey(item, 'node.slug'),
    key: getKey(item, 'node.id'),
  }));

  const tagOptions = tags.map((item) => ({
    label: getKey(item, `node.title_${locale}`),
    value: base + getKey(item, 'node.slug'),
    key: getKey(item, 'node.id'),
  }));

  if (category !== null && tag === null) {
    basePath = `/${locale}/experience${category.slug}/`;
  }
  if (tag !== null && category === null) {
    basePath = `/${locale}/experience${tag.slug}/`;
  }

  const title = getKey(social, `title_${locale}`);
  const socialItems = [
    {
      key: getKey(social, `twitter_title_${locale}`),
      title: getKey(social, `twitter_title_${locale}`),
      account: getKey(social, `twitter_${locale}`),
      logo: getKey(social, 'twitter_icon.publicURL'),
      backgroundImage: getKey(social, 'twitter_background.publicURL'),
      link: getKey(social, 'twitter_url'),
    },
    {
      key: getKey(social, `facebook_title_${locale}`),
      title: getKey(social, `facebook_title_${locale}`),
      account: getKey(social, `facebook_${locale}`),
      logo: getKey(social, 'facebook_icon.publicURL'),
      backgroundImage: getKey(social, 'facebook_background.publicURL'),
      link: getKey(social, 'facebook_url'),
    },
    {
      key: getKey(social, `instagram_title_${locale}`),
      title: getKey(social, `instagram_title_${locale}`),
      account: getKey(social, `instagram_${locale}`),
      logo: getKey(social, 'instagram_icon.publicURL'),
      backgroundImage: getKey(social, 'instagram_background.publicURL'),
      link: getKey(social, 'instagram_url'),
    },
  ];

  return (
    <Fragment>
      <Helmet
        title={label}
        meta={[
          { name: 'description', content: label },
          {
            name: 'keywords',
            content: 'cbx, crossborder express, tijuana, mexico, usa',
          },
          { name: 'viewport', content: 'width=device-width' },
        ]}
      >
        <html lang={locale} />
      </Helmet>
      <Layout {...props}>
        <GlobalStyles>
          <section style={{ width: 'auto', minHeight: 800, overflow: 'hidden' }}>
            <ExperienceLayout
              categories={options}
              tags={tagOptions}
              articles={elements}
              locale={locale}
              category={category}
              tag={tag}
              base={base}
            />
            <div className="container">
              <ExperiencePagination path={basePath} page={page} count={pages.length} />
            </div>
          </section>
        </GlobalStyles>
      </Layout>
    </Fragment>
  );
};

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "services"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

Experience.propTypes = {
  pageContext: PropTypes.object,
};

export default Experience;
